export interface VideoMetadata {
    durationS: number;
    widthPx: number;
    heightPx: number;
}

export class VideoDecoder {
    private video: HTMLVideoElement | null = null;
    private objectUrl: string | null = null;
    private canvas: HTMLCanvasElement | null = null;

    async load(file: File): Promise<VideoMetadata> {
        return this.loadSource(URL.createObjectURL(file), { isObjectUrl: true });
    }

    /**
     * Load a remote video by URL. Our editor media (user uploads / stock) is
     * served with CORS headers, so — like Luminary — we set crossOrigin and
     * stream the URL straight into the element for canvas readback, with no
     * proxy and no full download.
     */
    async loadUrl(url: string): Promise<VideoMetadata> {
        return this.loadSource(url, { isObjectUrl: false });
    }

    private async loadSource(
        src: string,
        { isObjectUrl }: { isObjectUrl: boolean },
    ): Promise<VideoMetadata> {
        this.dispose();
        this.video = document.createElement('video');
        this.video.muted = true;
        this.video.playsInline = true;
        this.video.preload = 'auto';
        if (!isObjectUrl) {
            this.video.crossOrigin = 'anonymous';
        }
        if (isObjectUrl) {
            this.objectUrl = src;
        }
        this.video.src = src;

        await new Promise<void>((resolve, reject) => {
            const v = this.video!;
            const onLoaded = () => {
                v.removeEventListener('loadedmetadata', onLoaded);
                v.removeEventListener('error', onError);
                resolve();
            };
            const onError = () => {
                v.removeEventListener('loadedmetadata', onLoaded);
                v.removeEventListener('error', onError);
                reject(new Error('Failed to load video metadata'));
            };
            v.addEventListener('loadedmetadata', onLoaded);
            v.addEventListener('error', onError);
        });

        this.canvas = document.createElement('canvas');
        return {
            durationS: this.video.duration,
            widthPx: this.video.videoWidth,
            heightPx: this.video.videoHeight,
        };
    }

    async extractFrameAt(timeS: number): Promise<HTMLCanvasElement> {
        if (!this.video || !this.canvas) {
            throw new Error('VideoDecoder.extractFrameAt called before load');
        }
        const v = this.video;
        const canvas = document.createElement('canvas');
        canvas.width = v.videoWidth;
        canvas.height = v.videoHeight;

        await new Promise<void>((resolve, reject) => {
            const onSeeked = () => {
                v.removeEventListener('seeked', onSeeked);
                v.removeEventListener('error', onError);
                resolve();
            };
            const onError = () => {
                v.removeEventListener('seeked', onSeeked);
                v.removeEventListener('error', onError);
                reject(new Error('Failed to seek video'));
            };
            v.addEventListener('seeked', onSeeked);
            v.addEventListener('error', onError);
            v.currentTime = Math.max(0, Math.min(v.duration, timeS));
        });

        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('Could not acquire 2D context for video frame');
        ctx.drawImage(v, 0, 0, canvas.width, canvas.height);
        return canvas;
    }

    dispose(): void {
        if (this.objectUrl) {
            URL.revokeObjectURL(this.objectUrl);
            this.objectUrl = null;
        }
        if (this.video) {
            this.video.src = '';
            this.video.removeAttribute('src');
            this.video = null;
        }
        this.canvas = null;
    }
}
